/**
 * FikoStabilTopolojiDebug.ts — Stabil ID görsel test overlay (DEV, salt eklenti)
 *
 * Mevcut FikoSecililer / hover sistemine HİÇ dokunmaz. Kendi throttled
 * pointermove dinleyicisi + kendi raycaster'ı + kendi DOM overlay'i ile çalışır.
 * baslat()/durdur() ile aç-kapa. Kapalıyken sıfır maliyet.
 *
 * Amaç: kaydedilmiş stabil ID'lerin gerçek modelde doğru çözümlendiğini
 * gözle görmek (hover ettiğin yüzün partId/solidId/faceId/stabilId'si köşede).
 */

import * as THREE from 'three';
import { FikoStabilTopoloji } from './FikoStabilTopoloji';

export class FikoStabilTopolojiDebug {
  private overlay: HTMLDivElement | null = null;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  private sonZaman = 0;
  private aktif = false;
  private dinleyici: ((e: PointerEvent) => void) | null = null;

  constructor(
    private topo: FikoStabilTopoloji,
    private canvasAl: () => HTMLCanvasElement | null | undefined,
    private kameraAl: () => THREE.Camera | null | undefined,
    private kokAl: () => THREE.Object3D | null | undefined,
  ) {}

  get acikMi(): boolean { return this.aktif; }

  baslat(): void {
    if (this.aktif) return;
    const canvas = this.canvasAl();
    if (!canvas) { console.warn('[StabilTopoloji] canvas bulunamadı'); return; }

    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed', top: '12px', right: '12px', zIndex: '99999',
      padding: '10px 14px', borderRadius: '10px', font: '12px/1.6 monospace',
      background: 'rgba(15,18,28,.88)', color: '#e6f0ff', pointerEvents: 'none',
      border: '1px solid rgba(120,160,255,.35)', backdropFilter: 'blur(6px)',
      boxShadow: '0 6px 24px rgba(0,0,0,.4)', minWidth: '210px',
    } as CSSStyleDeclaration);
    this.overlay.innerHTML = this.bilgiSatiri('hover bekleniyor…');
    document.body.appendChild(this.overlay);

    this.dinleyici = (e: PointerEvent) => this.uzerineGel(e, canvas);
    canvas.addEventListener('pointermove', this.dinleyici, { passive: true });
    this.aktif = true;
  }

  durdur(): void {
    if (this.dinleyici) {
      this.canvasAl()?.removeEventListener('pointermove', this.dinleyici as any);
      this.dinleyici = null;
    }
    this.overlay?.remove();
    this.overlay = null;
    this.aktif = false;
  }

  private uzerineGel(e: PointerEvent, canvas: HTMLCanvasElement): void {
    const now = performance.now();
    if (now - this.sonZaman < 33) return; // ~30Hz throttle
    this.sonZaman = now;

    const kamera = this.kameraAl();
    const kok = this.kokAl();
    if (!kamera || !kok || !this.overlay) return;

    const rect = canvas.getBoundingClientRect();
    this.mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, kamera as THREE.Camera);

    const hedefler: THREE.Object3D[] = [];
    kok.traverse((o) => { if ((o as THREE.Mesh).isMesh && o.visible) hedefler.push(o); });

    const kesisimler = this.raycaster.intersectObjects(hedefler, false);
    const hit = kesisimler.find(k => k.faceIndex != null);
    if (!hit || hit.faceIndex == null) {
      this.overlay.innerHTML = this.bilgiSatiri('—');
      return;
    }

    // faceIndex (üçgen indeksi) → ilk vertex → grup.z üzerinden çözümle
    const mesh = hit.object as THREE.Mesh;
    const geo = mesh.geometry as THREE.BufferGeometry;
    const idx = geo.index;
    const ilkVertex = idx ? idx.getX(hit.faceIndex * 3) : hit.faceIndex * 3;
    const k = this.topo.cozumleVertex(mesh, ilkVertex);

    if (!k) {
      this.overlay.innerHTML = this.bilgiSatiri('kayıt yok (önce kaydetSahne)');
      return;
    }
    this.overlay.innerHTML =
      `<div style="font-weight:700;margin-bottom:4px;color:#7db0ff">🔖 Stabil Topoloji</div>` +
      `<div>partId&nbsp;&nbsp;: <b>${k.partId}</b></div>` +
      `<div>solidId&nbsp;: <b>${k.solidId}</b></div>` +
      `<div>faceId&nbsp;&nbsp;: <b>${k.faceId}</b></div>` +
      `<div style="color:#9affc0">stabilId: <b>${k.stabilId}</b></div>` +
      `<div style="opacity:.6;margin-top:4px">parça:${this.topo.parcaSayisi} • yüz:${this.topo.yuzSayisi}</div>`;
  }

  private bilgiSatiri(durum: string): string {
    return `<div style="font-weight:700;margin-bottom:4px;color:#7db0ff">🔖 Stabil Topoloji</div>` +
      `<div style="opacity:.7">${durum}</div>` +
      `<div style="opacity:.6;margin-top:4px">parça:${this.topo.parcaSayisi} • yüz:${this.topo.yuzSayisi}</div>`;
  }
}
